
import { useState, useEffect, useRef, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { User, Session } from "@supabase/supabase-js";
import { secureLog } from "@/utils/secureLogger";

const MAX_REFRESH_ATTEMPTS = 3;
const PROACTIVE_REFRESH_THRESHOLD = 300; // 5 minutes
const PERIODIC_CHECK_THRESHOLD = 600; // 10 minutes
const PERIODIC_CHECK_INTERVAL = 5 * 60 * 1000; // 5 minutes

export function useAuthState() {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const hasInitialized = useRef(false);
  const refreshAttempts = useRef(0);
  
  // Robust session refresh with retry logic and exponential backoff
  const refreshSessionWithRetry = useCallback(async (): Promise<boolean> => {
    const attemptRefresh = async (attempt: number): Promise<boolean> => {
      try {
        console.log(`[Auth] Attempting session refresh (attempt ${attempt + 1}/${MAX_REFRESH_ATTEMPTS})`);
        
        const { data, error } = await supabase.auth.refreshSession();
        
        if (error) {
          console.warn(`[Auth] Refresh attempt ${attempt + 1} failed:`, error.message);
          
          // Check if error is retryable (network issues)
          const isRetryable = error.message?.includes('network') || 
              error.message?.includes('fetch') ||
              error.message?.includes('Failed to fetch') ||
              error.status === 0 ||
              error.status === 503;
              
          if (isRetryable && attempt < MAX_REFRESH_ATTEMPTS - 1) {
            const delay = Math.pow(2, attempt) * 1000; // 1s, 2s, 4s
            console.log(`[Auth] Retrying in ${delay}ms...`);
            await new Promise(resolve => setTimeout(resolve, delay));
            return attemptRefresh(attempt + 1);
          }
          return false;
        }
        
        if (data?.session) {
          console.log('[Auth] Session refreshed successfully');
          setSession(data.session);
          setUser(data.session.user);
          refreshAttempts.current = 0;
          return true;
        }
        
        return false;
      } catch (err) {
        console.error(`[Auth] Refresh exception on attempt ${attempt + 1}:`, err);
        if (attempt < MAX_REFRESH_ATTEMPTS - 1) {
          const delay = Math.pow(2, attempt) * 1000;
          await new Promise(resolve => setTimeout(resolve, delay));
          return attemptRefresh(attempt + 1);
        }
        return false;
      }
    };
    
    return attemptRefresh(0);
  }, []);

  useEffect(() => {
    console.log("[Auth] Initializing auth state listener");
    secureLog.debug("Setting up auth state listener");
    
    let mounted = true;
    
    // 1. Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, currentSession) => {
        console.log("[Auth] State change event:", { 
          event, 
          hasSession: !!currentSession,
          userId: currentSession?.user?.id?.slice(0, 8)
        });
        
        if (!mounted) return;
        
        switch (event) {
          case 'SIGNED_IN':
          case 'TOKEN_REFRESHED':
            setSession(currentSession);
            setUser(currentSession?.user || null);
            refreshAttempts.current = 0;
            break;
            
          case 'SIGNED_OUT':
            // Only clear on explicit sign out
            console.log("[Auth] User explicitly signed out");
            setSession(null);
            setUser(null);
            break;
            
          case 'INITIAL_SESSION':
            setSession(currentSession);
            setUser(currentSession?.user || null);
            break;
            
          default:
            if (currentSession) {
              setSession(currentSession);
              setUser(currentSession.user);
            }
        }
        
        setIsLoading(false);
      }
    );

    // 2. Check for existing session
    const checkSession = async () => {
      if (!mounted || hasInitialized.current) return;
      hasInitialized.current = true;
      
      try {
        const { data, error } = await supabase.auth.getSession();
        console.log("[Auth] Initial session check:", { 
          hasSession: !!data?.session,
          error: error?.message
        });
        
        if (mounted && data?.session) {
          setSession(data.session);
          setUser(data.session.user);
          
          // Start auto refresh after getting session
          await supabase.auth.startAutoRefresh();
          console.log("[Auth] Auto refresh started");
        }
        
        if (mounted) setIsLoading(false);
      } catch (err) {
        console.error("[Auth] Session check error:", err);
        if (mounted) setIsLoading(false);
      }
    };
    
    checkSession();
    
    // 3. Handle visibility changes - critical for inactive tabs
    const handleVisibilityChange = async () => {
      if (!mounted) return;
      
      if (document.visibilityState === 'visible') {
        console.log("[Auth] Tab became visible, ensuring session is valid");
        
        // Always restart auto refresh when tab becomes visible
        try {
          await supabase.auth.startAutoRefresh();
        } catch (err) {
          console.warn("[Auth] Failed to start auto refresh:", err);
        }
        
        // Check current session state
        try {
          const { data: { session: currentSession } } = await supabase.auth.getSession();
          
          if (currentSession) {
            const expiresAt = currentSession.expires_at;
            const now = Math.floor(Date.now() / 1000);
            const expiresIn = expiresAt ? expiresAt - now : Infinity;
            
            console.log("[Auth] Session expires in:", expiresIn, "seconds");
            
            // If expired or expiring within 5 minutes, refresh proactively
            if (expiresIn < PROACTIVE_REFRESH_THRESHOLD) {
              console.log("[Auth] Token expired or expiring soon, refreshing...");
              const success = await refreshSessionWithRetry();
              if (!success && mounted) {
                console.warn("[Auth] Refresh failed, keeping existing session state");
              }
            } else {
              setSession(currentSession);
              setUser(currentSession.user);
            }
          }
        } catch (err) {
          console.warn("[Auth] Visibility change session check failed:", err);
        }
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    // 4. Periodic session check (safety net)
    const sessionCheckInterval = setInterval(async () => {
      if (!mounted || document.visibilityState !== 'visible') return;
      
      try {
        const { data: { session: currentSession } } = await supabase.auth.getSession();
        if (currentSession && mounted) {
          const expiresAt = currentSession.expires_at;
          const now = Math.floor(Date.now() / 1000);
          const expiresIn = expiresAt ? expiresAt - now : Infinity;
          
          // If token expiring within 10 minutes, refresh it
          if (expiresIn > 0 && expiresIn < PERIODIC_CHECK_THRESHOLD) {
            console.log("[Auth] Periodic check: token expiring soon, refreshing...");
            await refreshSessionWithRetry();
          }
        }
      } catch (err) {
        console.warn("[Auth] Periodic session check failed:", err);
      }
    }, PERIODIC_CHECK_INTERVAL);
    
    return () => {
      console.log("[Auth] Cleanup");
      mounted = false;
      subscription.unsubscribe();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      clearInterval(sessionCheckInterval);
    };
  }, [refreshSessionWithRetry]);
  
  return { user, session, isLoading };
}
